import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import {
  resetSelectedProduct,
  setSelectedProduct,
} from '../data/guaranteePurchases/actions';
import { productFamilyThemeClass } from '../constants/productFamily';
import useFeatureFlag from './useFeatureFlag';
import { DB_GUARANTEES_V2, DB_GUARANTEES_API_V2 } from '../constants/featureFlags';

const selectGuaranteePurchases = state => state.data.guaranteePurchases;

export const useGuaranteeProduct = () => {
  const { selectedProduct } = useSelector(selectGuaranteePurchases);
  const dispatch = useDispatch();
  const setProduct = useCallback(
    productId => {
      dispatch(setSelectedProduct(productId));
    },
    [dispatch]
  );
  return [selectedProduct, setProduct];
};

// Drops the product context when the page is left, so the next entry — a reload or an
// in-app navigation back — opens on the default again. The reset runs on cleanup rather
// than on mount so the next visit's first render, and its first ineligible-purchases
// request, can never read a leftover value.
export const useResetGuaranteeProductOnExit = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetSelectedProduct());
    };
  }, [dispatch]);
};

export const useGuaranteesApiV2 = () => {
  const [isGuaranteesV2Enabled, loaded] = useFeatureFlag(DB_GUARANTEES_V2);
  const [isApiV2Enabled] = useFeatureFlag(DB_GUARANTEES_API_V2);

  return [isGuaranteesV2Enabled && isApiV2Enabled, loaded];
};

export const useGuaranteeTheme = () => {
  const [selectedProduct] = useGuaranteeProduct();
  const [isGuaranteesV2Enabled] = useFeatureFlag(DB_GUARANTEES_V2);

  const themeClass = useMemo(() => {
    if (!isGuaranteesV2Enabled) return '';
    return `theme ${productFamilyThemeClass(selectedProduct)}`;
  }, [selectedProduct, isGuaranteesV2Enabled]);

  return themeClass;
};
